import { createI18n } from 'vue-i18n';
import { Settings } from 'luxon';
import { useStorage } from '@vueuse/core';
import { setLocale } from 'yup';
import { isNonNullable, isNullable } from '@/utils/nullability-helpers';
import { computed } from 'vue';
import type { PossibleLanguage } from '@shared-types/scenario-definition';
import type { MessageSchema } from './lang/translation-types';

export type { PossibleLanguage } from '@shared-types/scenario-definition';

const fallbackLanguage: PossibleLanguage = 'en';

export const possibleLanguages: PossibleLanguage[] = [
    fallbackLanguage,
    // 'ar',
    'de',
    // 'fr',
    // 'it',
    // 'es',
    // 'cs',
    // 'pt',
    // 'ja',
    // 'zh-Hans',
    // 'zh-Hant',
    'hi',
];

const rtlLanguages: PossibleLanguage[] = ['ar'];

export const i18n = createI18n({
    legacy: false,
    locale: fallbackLanguage, // set locale
    fallbackLocale: fallbackLanguage,
    messages: {}, // setting to empty as it will be loaded based on the route
    silentTranslationWarn: import.meta.env.PROD,
    warnHtmlMessage: false,
});

export function isPossibleLanguage(lang: string): lang is PossibleLanguage {
    return possibleLanguages.includes(lang as PossibleLanguage);
}

export type TranslationKey = FlattenKeys<MessageSchema>;

type FlattenKeys<T> = T extends object
    ? { [K in keyof T]: T[K] extends object ? `${K & string}.${FlattenKeys<T[K]> & string}` : K }[keyof T]
    : '';

export const currentLanguage = useStorage<PossibleLanguage>(
    'sagacity-simulation:language',
    getFirstSupportedOfLanguages(possibleLanguages),
);

/**
 * True once the user has explicitly chosen a language in-app (see useLanguageSettings.changeLanguage).
 * Distinguishes a deliberate choice from the auto-seeded browser default, so an organization's
 * default language only ever applies to users who have never picked one. Stored per browser; for
 * signed-in users the authoritative signal is users.preferred_language, which useLanguageResolution()
 * reconciles into this flag on load in both directions (set when non-null, cleared when null).
 */
export const languagePreferenceIsExplicit = useStorage<boolean>('sagacity-simulation:language-explicit', false);

export function markLanguagePreferenceExplicit(): void {
    languagePreferenceIsExplicit.value = true;
}

export function clearLanguagePreferenceExplicit(): void {
    languagePreferenceIsExplicit.value = false;
}

export const isRtl = computed(() => rtlLanguages.includes(currentLanguage.value));

// Phase 1 modules
export type I18nModuleName = 'common' | 'auth' | 'app';

// Per-language set of loaded module names
const loadedModules = new Map<string, Set<I18nModuleName>>();

// All modules that have ever been activated (needed) during this session.
// Used to re-load the right set of modules when switching language.
const activeModules = new Set<I18nModuleName>();

async function loadI18nModule(lang: PossibleLanguage, moduleName: I18nModuleName): Promise<void> {
    const loaded = loadedModules.get(lang) ?? new Set<I18nModuleName>();
    if (loaded.has(moduleName)) return;

    try {
        const mod = await import(`./lang/${lang}/${moduleName}.json`);
        i18n.global.mergeLocaleMessage(lang, mod.default);
        loaded.add(moduleName);
        loadedModules.set(lang, loaded);
        activeModules.add(moduleName);
    } catch (e) {
        console.error(`[i18n] Failed to load module "${moduleName}" for "${lang}"`, e);
        if (lang !== fallbackLanguage) {
            await loadI18nModule(fallbackLanguage, moduleName).catch(() => {});
        }
    }
}

/**
 * Ensures a given module is loaded for the given language.
 * Also pre-loads the fallback locale's common module so vue-i18n fallback works
 * for any keys missing from the requested locale.
 * Safe to call multiple times — cached after first load.
 */
export async function ensureI18nModuleLoaded(lang: PossibleLanguage, moduleName: I18nModuleName): Promise<void> {
    if (lang !== fallbackLanguage) {
        await loadI18nModule(fallbackLanguage, 'common');
    }
    if (moduleName !== 'common') {
        await loadI18nModule(lang, 'common');
    }
    await loadI18nModule(lang, moduleName);
}

const loadedLanguages: string[] = [];

/**
 * Loads the `common` module for a language, plus any other modules that have already
 * been activated during this session (e.g. 'app' when navigating to /app/*).
 * This ensures a language switch re-loads all modules that are currently in use,
 * not just common.
 */
export async function loadLanguageAsync(lang: string): Promise<void> {
    if (!isPossibleLanguage(lang)) return;

    if (!loadedLanguages.includes(lang)) {
        // Always load common; also load every module already activated this session
        // so that a language switch never leaves feature translations missing.
        const modules: Array<Promise<void>> = [loadI18nModule(lang, 'common')];
        for (const mod of activeModules) {
            if (mod !== 'common') modules.push(loadI18nModule(lang, mod));
        }
        await Promise.all(modules);
        loadedLanguages.push(lang);
    }

    // Always call setI18nLanguage to ensure dir/lang attributes are set on <html>.
    // postcss-rtl wraps all CSS in [dir] selectors — without this the styles don't apply.
    setI18nLanguage(lang);
}

/**
 * Applies an organization's configured default language for a user who has not set their own
 * preference. No-op when the user has an explicit preference, when the value is missing / not a
 * supported language, when it falls outside the org's `languageRestrictions`, or when it is
 * already active. Safe to call reactively / repeatedly — used both on org-scoped pre-login pages
 * and by an app-wide watcher on the signed-in user's current org.
 *
 * The `languageRestrictions` check is a runtime backstop: the config UIs already filter the
 * selectable options, but an org's stored default can predate a later restriction tightening.
 */
export async function applyOrganizationDefaultLanguage(
    defaultLanguage?: string | null,
    languageRestrictions?: string[] | null,
): Promise<void> {
    if (languagePreferenceIsExplicit.value) return;
    if (isNullable(defaultLanguage) || !isPossibleLanguage(defaultLanguage)) return;
    if (
        isNonNullable(languageRestrictions) &&
        languageRestrictions.length > 0 &&
        !languageRestrictions.includes(defaultLanguage)
    ) {
        return;
    }
    if (defaultLanguage === currentLanguage.value) return;
    await loadLanguageAsync(defaultLanguage);
}

/**
 * Makes sure the language from local storage is valid, then loads the resolved language's common module.
 */
export async function initializeLanguage() {
    if (!isPossibleLanguage(currentLanguage.value)) {
        currentLanguage.value = getFirstSupportedOfLanguages(possibleLanguages);
    }

    const lang = isPossibleLanguage(currentLanguage.value) ? currentLanguage.value : fallbackLanguage;
    await loadLanguageAsync(lang);
}

function setI18nLanguage(lang: PossibleLanguage) {
    i18n.global.locale.value = lang;
    currentLanguage.value = lang;
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', rtlLanguages.includes(lang) ? 'rtl' : 'ltr');
    Settings.defaultLocale = lang;

    const messages = (i18n.global.messages.value as Record<PossibleLanguage, MessageSchema>)[lang];
    if (isNonNullable(messages?.validation)) {
        setLocale(messages.validation);
    }
    return lang;
}

/**
 * Returns the first language from the browser's configured languages which is part of `languages`.
 * If no supported language is found, returns the first in the list.
 */
export function getFirstSupportedOfLanguages<T extends string>(languages: T[]): T {
    for (const languageCode of navigator.languages) {
        const language = languageCode.split('-')[0] as T;
        if (languages.includes(language)) {
            return language;
        }
    }
    return languages[0];
}

export default i18n;
